import {
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  CloseRounded,
  DragIndicator,
  SearchRounded,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button as MuiButton,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import { Resources } from '../resource';
import { PERPS_V1_PINS_PERMISSION } from '../perps-v1-shared/constants';
import { errorMessage } from '../perps-v1-shared/errorMessage';
import { MarketView } from '../perps-v1-shared/types';
import { Pin, PinCandidate, pinsApi } from './dataProvider';
import './init';
import { flattenViewTree, joinViewPath } from './viewTree';

interface PinRow {
  symbol: string;
  label: string;
  dex: string;
  isListed: boolean;
  // Backend spec INERTPIN: false means the symbol is not in the view's interpreted result right
  // now, so this pin (though saved fine) promotes nothing. Sourced from HotPin.isInView, not
  // re-derived client-side — the same reasoning as isListed below: a second evaluator would drift
  // from what the app actually serves.
  isInView: boolean;
}

function SortablePinRow({
  row,
  index,
  count,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  row: PinRow;
  index: number;
  count: number;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRemove: (symbol: string) => void;
}) {
  const translate = useTranslate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.symbol,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Delisted (isListed false) already implies "not in view" upstream — the delisted chip alone
  // covers that case, so the not-in-view treatment only kicks in for a symbol that is still live
  // but simply outside this view's current result, to avoid stacking two badges that say
  // overlapping things on the same row.
  const isInert = row.isListed && !row.isInView;

  return (
    <div ref={setNodeRef} style={style}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          px: 1.5,
          py: 1.25,
          border: '1px solid',
          borderRadius: 1.5,
          borderColor: !row.isListed ? 'warning.main' : isInert ? 'info.main' : 'divider',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ width: 24, textAlign: 'center', color: 'primary.main' }}
        >
          {index + 1}
        </Typography>
        <IconButton
          size="small"
          sx={{ cursor: 'grab', color: 'text.disabled' }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {row.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.dex ? `${row.symbol} · ${row.dex}` : row.symbol}
          </Typography>
          {isInert ? (
            <Typography
              variant="caption"
              color="info.main"
              sx={{ display: 'block', mt: 0.25 }}
            >
              {translate('module.perpsV1Pins.notInViewHint')}
            </Typography>
          ) : null}
        </Box>
        {!row.isListed ? (
          <Chip size="small" color="warning" label={translate('module.perpsV1Pins.delisted')} />
        ) : null}
        {isInert ? (
          <Chip
            size="small"
            color="info"
            variant="outlined"
            label={translate('module.perpsV1Pins.notInView')}
          />
        ) : null}
        <PermissionButton resource={PERPS_V1_PINS_PERMISSION} action="update">
          <Stack direction="row" spacing={0}>
            <IconButton
              size="small"
              disabled={index === 0}
              onClick={() => onMoveUp(index)}
              aria-label={translate('module.perpsV1Pins.moveUp')}
            >
              <ArrowUpwardRounded fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={index === count - 1}
              onClick={() => onMoveDown(index)}
              aria-label={translate('module.perpsV1Pins.moveDown')}
            >
              <ArrowDownwardRounded fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onRemove(row.symbol)}
              aria-label={translate('module.perpsV1Pins.remove')}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Stack>
        </PermissionButton>
      </Stack>
    </div>
  );
}

function CandidateRow({
  candidate,
  pinnedIndex,
  onPin,
}: {
  candidate: PinCandidate;
  pinnedIndex?: number;
  onPin: (symbol: string) => void;
}) {
  const translate = useTranslate();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Box sx={{ flex: 1, minWidth: 0, opacity: pinnedIndex ? 0.55 : 1 }}>
        <Typography variant="subtitle2" noWrap>
          {candidate.displaySymbol}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {candidate.dex ? `${candidate.symbol} · ${candidate.dex}` : candidate.symbol}
        </Typography>
      </Box>
      {pinnedIndex ? (
        <Chip
          size="small"
          variant="outlined"
          label={translate('module.perpsV1Pins.pinnedBadge', { index: pinnedIndex })}
          sx={{ color: 'text.disabled', borderColor: 'divider' }}
        />
      ) : (
        <PermissionButton resource={PERPS_V1_PINS_PERMISSION} action="update">
          <MuiButton
            size="small"
            variant="outlined"
            color="success"
            onClick={() => onPin(candidate.symbol)}
          >
            {translate('module.perpsV1Pins.pin')}
          </MuiButton>
        </PermissionButton>
      )}
    </Stack>
  );
}

// 打开置顶配置页时默认选中的榜单 code（种子数据里的「热门」）。
const DEFAULT_PIN_VIEW_CODE = 'hot';

const View = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [views, setViews] = useState<MarketView[]>([]);
  const [viewsLoading, setViewsLoading] = useState(true);
  const [selectedViewCode, setSelectedViewCode] = useState<string>('');

  const [candidates, setCandidates] = useState<PinCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(true);

  const [serverPins, setServerPins] = useState<string[]>([]);
  const [draftPins, setDraftPins] = useState<string[]>([]);
  // symbol -> isInView, snapshotted from the last GET/PUT response (spec INERTPIN). A symbol
  // pinned from the candidate pool during this session has no entry yet — its real membership is
  // unknown until the next load/save — and defaults to true (no warning) rather than guessing
  // inert, since the candidate pool is the whole universe and says nothing about any one view's
  // interpreted result.
  const [inViewMap, setInViewMap] = useState<Map<string, boolean>>(new Map());
  const [pinsLoading, setPinsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [keyword, setKeyword] = useState('');

  // Guards against a stale /pins?viewCode= response landing after a fast view switch.
  const requestSeq = useRef(0);

  // Tree order (each top-level view followed by its own children, recursively), not a flat
  // displayOrder sort — displayOrder is only meaningful within one sibling group, so e.g. every
  // section's "All" child sharing order 1 would otherwise collide with each other and with the
  // top-level views. Every enabled view stays offered, groups included; only the order and the
  // label (see joinViewPath below) change.
  const pinnableViews = useMemo(
    () => flattenViewTree(views.filter((v) => v.enabled)),
    [views]
  );

  // Load the view list + the global candidate pool once on mount.
  useEffect(() => {
    let active = true;

    const load = async () => {
      setViewsLoading(true);
      setCandidatesLoading(true);
      try {
        const [viewsResponse, candidatesResponse] = await Promise.all([
          dataProvider.getList(Resources.perpsV1Views, {
            filter: {},
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'displayOrder', order: 'ASC' },
          }),
          dataProvider.getList(Resources.perpsV1Pins, {
            filter: {},
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'symbol', order: 'ASC' },
          }),
        ]);
        if (!active) return;
        const loadedViews: MarketView[] = viewsResponse?.data ?? [];
        setViews(loadedViews);
        setCandidates(candidatesResponse?.data ?? []);
        const eligible = flattenViewTree(loadedViews.filter((v) => v.enabled));
        // 默认选中「热门」：它是实际最常配置置顶的榜单。若它不存在或被停用，
        // 再退回树形展示顺序最靠前的那个。
        const preferred =
          eligible.find((n) => n.view.code === DEFAULT_PIN_VIEW_CODE)?.view ?? eligible[0]?.view;
        if (preferred) setSelectedViewCode(preferred.code);
      } catch (error: any) {
        if (!active) return;
        notify(errorMessage(error, translate('module.perpsV1Pins.loadError')), {
          type: 'error',
        });
      } finally {
        if (active) {
          setViewsLoading(false);
          setCandidatesLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPins = useCallback((pins: Pin[]) => {
    const symbols = [...pins].sort((a, b) => a.sortIndex - b.sortIndex).map((p) => p.symbol);
    setServerPins(symbols);
    setDraftPins(symbols);
    setInViewMap(new Map(pins.map((p) => [p.symbol, p.isInView])));
  }, []);

  // Load pins for the currently selected view whenever it changes.
  useEffect(() => {
    if (!selectedViewCode) return undefined;
    let active = true;
    requestSeq.current += 1;
    const seq = requestSeq.current;

    const load = async () => {
      setPinsLoading(true);
      try {
        const response = await pinsApi.list(selectedViewCode);
        if (!active || requestSeq.current !== seq) return;
        applyPins(response?.data ?? []);
      } catch (error: any) {
        if (!active || requestSeq.current !== seq) return;
        notify(errorMessage(error, translate('module.perpsV1Pins.loadError')), {
          type: 'error',
        });
      } finally {
        if (active && requestSeq.current === seq) setPinsLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [selectedViewCode, applyPins, notify, translate]);

  const candidateMap = useMemo(() => new Map(candidates.map((c) => [c.symbol, c])), [candidates]);

  // Draft-order badge for the candidates panel; must reflect the in-progress draft, not the saved order.
  const draftIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    draftPins.forEach((symbol, index) => map.set(symbol, index + 1));
    return map;
  }, [draftPins]);

  const isChanged = useMemo(
    () => JSON.stringify(draftPins) !== JSON.stringify(serverPins),
    [draftPins, serverPins]
  );

  const filteredCandidates = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    if (!key) return candidates;
    return candidates.filter(
      (c) => c.symbol.toLowerCase().includes(key) || c.displaySymbol.toLowerCase().includes(key)
    );
  }, [candidates, keyword]);

  const pinRows: PinRow[] = useMemo(
    () =>
      draftPins.map((symbol) => {
        const candidate = candidateMap.get(symbol);
        return {
          symbol,
          label: candidate?.displaySymbol ?? symbol,
          dex: candidate?.dex ?? '',
          isListed: !!candidate,
          isInView: inViewMap.get(symbol) ?? true,
        };
      }),
    [draftPins, candidateMap, inViewMap]
  );

  // Step-4 case: every stored pin in this view does nothing (not just a mix). A per-row chip is
  // easy to miss when scanning a short "looks fully configured" list, and this is a materially
  // different signal from "one pin among several is inert" — the whole view's pin config is dead —
  // so it gets a banner on top of, not instead of, the per-row chips.
  const allPinsInert = useMemo(
    () => pinRows.length > 0 && pinRows.every((row) => !row.isInView),
    [pinRows]
  );

  const handlePin = (symbol: string) => {
    setDraftPins((prev) => (prev.includes(symbol) ? prev : [...prev, symbol]));
  };

  const handleRemove = (symbol: string) => {
    setDraftPins((prev) => prev.filter((s) => s !== symbol));
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    setDraftPins((prev) => {
      const next = index + direction;
      if (next < 0 || next >= prev.length) return prev;
      return arrayMove(prev, index, next);
    });
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setDraftPins((prev) => {
        const oldIndex = prev.indexOf(`${active.id}`);
        const newIndex = prev.indexOf(`${over.id}`);
        if (oldIndex < 0 || newIndex < 0) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleDiscard = () => {
    setDraftPins(serverPins);
  };

  const handleSave = async () => {
    if (!selectedViewCode) return;
    setSaving(true);
    try {
      const response = await pinsApi.save(selectedViewCode, draftPins);
      applyPins(response?.data ?? []);
      notify(translate('module.perpsV1Pins.saveSuccess'), { type: 'success' });
    } catch (error: any) {
      notify(errorMessage(error, translate('module.perpsV1Pins.saveError')), {
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSelectView = (event: ChangeEvent<HTMLInputElement>) => {
    const code = event.target.value;
    if (code === selectedViewCode) return;
    if (isChanged) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(translate('module.perpsV1Pins.confirmSwitchView'))) return;
    }
    setSelectedViewCode(code);
  };

  const loading = viewsLoading || candidatesLoading || pinsLoading;

  return (
    <PermissionGuard resource={PERPS_V1_PINS_PERMISSION} action="read">
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.perpsV1Pins.title')}
          links={[{ name: translate('resource.perpsV1') }, { name: translate('resource.perpsV1Pins') }]}
          action={
            <PermissionButton resource={PERPS_V1_PINS_PERMISSION} action="update">
              <Stack direction="row" spacing={2}>
                <MuiButton
                  variant="outlined"
                  color="inherit"
                  onClick={handleDiscard}
                  disabled={loading || saving || !isChanged}
                >
                  {translate('module.perpsV1Pins.discard')}
                </MuiButton>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  loading={saving}
                  disabled={loading || !isChanged || !selectedViewCode}
                >
                  {translate('module.perpsV1Pins.save')}
                </LoadingButton>
              </Stack>
            </PermissionButton>
          }
          sx={{ mb: 1 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate('module.perpsV1Pins.subtitle')}
        </Typography>

        {!viewsLoading && pinnableViews.length === 0 ? (
          <Card sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('module.perpsV1Pins.noEnabledViews')}
            </Typography>
          </Card>
        ) : (
          <>
            <TextField
              select
              size="small"
              sx={{ mb: 3, minWidth: 280 }}
              label={translate('module.perpsV1Pins.selectView')}
              value={selectedViewCode}
              onChange={handleSelectView}
              disabled={viewsLoading}
            >
              {pinnableViews.map((node) => (
                // The label is the full ancestor path (e.g. "Perpetual › All"), not just the
                // view's own label: MUI renders only the selected MenuItem's children when the
                // dropdown is CLOSED, so a bare "All" would still leave three options
                // indistinguishable once picked. The `sx` indent is on top of that path, for the
                // open list only — it never replaces it.
                <MenuItem key={node.view.code} value={node.view.code} sx={{ pl: 2 + node.depth * 2 }}>
                  {joinViewPath(node.path)}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
              <Card sx={{ p: 3, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ flexWrap: 'wrap', gap: 1 }}
                >
                  <Typography variant="h6">{translate('module.perpsV1Pins.candidates')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {translate('module.perpsV1Pins.totalCount', { count: filteredCandidates.length })}
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  size="small"
                  sx={{ mt: 1.5, mb: 1.5 }}
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder={translate('module.perpsV1Pins.searchPlaceholder')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ flex: 1, minHeight: 420, position: 'relative' }}>
                  <Stack spacing={0} sx={{ position: 'absolute', inset: 0, overflowY: 'auto', pr: 0.5 }}>
                    {candidatesLoading ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        {translate('module.perpsV1Pins.loading')}
                      </Typography>
                    ) : filteredCandidates.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        {translate('module.perpsV1Pins.noResult')}
                      </Typography>
                    ) : (
                      filteredCandidates.map((candidate) => (
                        <CandidateRow
                          key={candidate.symbol}
                          candidate={candidate}
                          pinnedIndex={draftIndexMap.get(candidate.symbol)}
                          onPin={handlePin}
                        />
                      ))
                    )}
                  </Stack>
                </Box>
              </Card>

              <Card sx={{ p: 3, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ flexWrap: 'wrap', gap: 1 }}
                >
                  <Typography variant="h6">{translate('module.perpsV1Pins.pinned')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {translate('module.perpsV1Pins.totalCount', { count: draftPins.length })}
                  </Typography>
                </Stack>
                {allPinsInert ? (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                    {translate('module.perpsV1Pins.allInertWarning', { count: pinRows.length })}
                  </Alert>
                ) : null}
                <Box sx={{ flex: 1, minHeight: 420, position: 'relative', mt: 2 }}>
                  <Stack spacing={1.5} sx={{ position: 'absolute', inset: 0, overflowY: 'auto', pr: 0.5 }}>
                    {pinsLoading ? (
                      <Typography variant="body2" color="text.secondary">
                        {translate('module.perpsV1Pins.loading')}
                      </Typography>
                    ) : pinRows.length === 0 ? (
                      <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('module.perpsV1Pins.emptyPinned')}
                        </Typography>
                      </Box>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        modifiers={[restrictToVerticalAxis]}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext items={draftPins} strategy={verticalListSortingStrategy}>
                          <Stack spacing={1.5}>
                            {pinRows.map((row, index) => (
                              <SortablePinRow
                                key={row.symbol}
                                row={row}
                                index={index}
                                count={pinRows.length}
                                onMoveUp={(i) => handleMove(i, -1)}
                                onMoveDown={(i) => handleMove(i, 1)}
                                onRemove={handleRemove}
                              />
                            ))}
                          </Stack>
                        </SortableContext>
                      </DndContext>
                    )}
                  </Stack>
                </Box>
              </Card>
            </Box>
          </>
        )}
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
